/**
 * MessageBubble Component
 * Displays individual message with styling based on sender
 *
 * WIREFRAME COMPONENT - Easy to replace!
 * Customize colors in config/messagingConfig.ts
 */

import { useState } from 'react'
import { messagingColors, messagingText } from '@/config/messagingConfig'
import { useAuth } from '@/features/auth'
import { useMessaging } from './MessagingContext'
import { AttachmentDisplay } from './AttachmentDisplay'
import { ReactionAndReplyPicker } from './ReactionAndReplyPicker'
import { ReactionDisplay } from './ReactionDisplay'
import { ReplyDisplay } from './ReplyDisplay'
import { GalleryImageReplyDisplay } from './GalleryImageReplyDisplay'
import type { Message } from '@/types/socketEvents'

interface MessageBubbleProps {
  message: Message
}

export function MessageBubble({ message }: MessageBubbleProps) {
  const { user } = useAuth()
  const { toggleReaction, startReply, scrollToMessage, highlightedMessageId, recentlyUnlockedProducts, conversation } = useMessaging()
  const isSentByMe = message.sender_id === user?.id

  // Reaction and reply picker state
  const [showPicker, setShowPicker] = useState(false)
  const [pickerPosition, setPickerPosition] = useState<'above' | 'below'>('above')

  // Handle message click (show picker)
  const handleMessageClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    const rect = e.currentTarget.getBoundingClientRect()
    const spaceBelow = window.innerHeight - rect.bottom
    setPickerPosition(spaceBelow > 250 ? 'below' : 'above') // More space needed for reply button
    setShowPicker(true)
  }

  // Handle emoji selection
  const handleEmojiSelect = (emoji: string) => {
    toggleReaction(message.id, emoji)
  }

  // Handle reply button click
  const handleReply = () => {
    startReply(message)
  }

  // Handle toggle reaction from display
  const handleToggleFromDisplay = (emoji: string) => {
    toggleReaction(message.id, emoji)
  }

  // Handle click on reply preview to scroll to original
  const handleClickReply = (messageId: string) => {
    scrollToMessage(messageId)
  }

  // Format timestamp
  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp)
    const now = new Date()
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60)

    if (diffInHours < 24) {
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    } else if (diffInHours < 48) {
      return messagingText.conversation.yesterday
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    }
  }

  // Check if this message is highlighted
  const isHighlighted = highlightedMessageId === message.id

  // Check if message has paid product attachments (disable reply for these)
  const hasPaidProduct = message.attachments?.some(
    (att: any) => att.is_paid || att.product_id
  ) ?? false

  return (
    <div
      id={`message-${message.id}`}
      className={`flex ${isSentByMe ? 'justify-end' : 'justify-start'} mb-4 ${isSentByMe ? 'animate-slide-in-right' : 'animate-slide-in-left'} ${isHighlighted ? 'message-highlight' : ''}`}
    >
      <div className={`flex items-end gap-2 max-w-[70%] ${isSentByMe ? 'flex-row-reverse' : 'flex-row'}`}>
        {/* Avatar */}
        {!isSentByMe && (
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#D4EEFF] flex items-center justify-center text-[#2C7799] text-sm font-semibold overflow-hidden">
            {(message.sender?.avatar_url || conversation?.creator?.avatar_url) ? (
              <img
                src={message.sender?.avatar_url || conversation?.creator?.avatar_url || undefined}
                alt={message.sender?.username || conversation?.creator?.username || 'User'}
                className="w-full h-full object-cover"
              />
            ) : (
              <span>{message.sender?.username?.[0]?.toUpperCase() || conversation?.creator?.username?.[0]?.toUpperCase() || '?'}</span>
            )}
          </div>
        )}

        {/* Message content */}
        <div className="relative">
          {/* Gallery Image Reply Display - show if replying to gallery image */}
          {message.reply_to_gallery_image && (
            <GalleryImageReplyDisplay
              galleryImage={message.reply_to_gallery_image}
              isSentByMe={isSentByMe}
            />
          )}

          {/* Reply Display - show if this is a reply to message */}
          {message.reply_to && !message.reply_to_gallery_image && (
            <ReplyDisplay
              replyTo={message.reply_to}
              onClickReply={handleClickReply}
              isSentByMe={isSentByMe}
            />
          )}

          {/* Check if message is attachment-only (no text content) */}
          {(() => {
            const hasTextContent = message.content && message.content !== '[Attachment]' && message.content.trim() !== ''
            const hasAttachments = message.attachments && message.attachments.length > 0
            const isAttachmentOnly = hasAttachments && !hasTextContent

            // Attachment-only: render without bubble background
            if (isAttachmentOnly) {
              return (
                <div onClick={handleMessageClick} className="cursor-pointer">
                  <AttachmentDisplay
                    attachments={message.attachments || []}
                    isSentByMe={isSentByMe}
                    recentlyUnlockedProducts={recentlyUnlockedProducts}
                  />
                </div>
              )
            }

            // Has text or text+attachments: render with bubble
            return (
              <div
                onClick={handleMessageClick}
                className={`px-4 py-3 rounded-3xl cursor-pointer hover:opacity-90 transition-all duration-200 ${
                  message.is_priority
                    ? 'bg-gradient-to-r from-amber-100 to-yellow-100 border border-amber-300 rounded-br-md'
                    : isSentByMe
                    ? messagingColors.sentMessage + ' rounded-br-md'
                    : messagingColors.receivedMessage + ' rounded-bl-md'
                } ${isHighlighted ? 'ring-2 ring-blue-500 ring-offset-2' : ''}`}
              >
                {message.is_priority && (
                  <div className="flex items-center gap-1 mb-1 text-xs font-semibold text-amber-700">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                    <span>Priority Message</span>
                  </div>
                )}
                {hasTextContent && (
                  <p className="text-sm whitespace-pre-wrap break-words">{message.content.trim()}</p>
                )}
                {hasAttachments && (
                  <AttachmentDisplay
                    attachments={message.attachments || []}
                    isSentByMe={isSentByMe}
                    recentlyUnlockedProducts={recentlyUnlockedProducts}
                  />
                )}
              </div>
            )
          })()}

          {/* Timestamp */}
          <div className={`mt-1 px-2 ${messagingColors.timestamp} ${isSentByMe ? 'text-right' : 'text-left'}`}>
            {formatTime(message.created_at)}
            {isSentByMe && message.read_at && (
              <span className="ml-1">✓</span>
            )}
          </div>

          {/* Reaction Display - positioned at outer top corner, floating above message */}
          {user && message.reactions && message.reactions.length > 0 && (
            <div className={`absolute -top-4 ${isSentByMe ? '-left-3' : '-right-3'}`}>
              <ReactionDisplay
                reactions={message.reactions}
                currentUserId={user.id}
                onToggleReaction={handleToggleFromDisplay}
              />
            </div>
          )}

          {/* Reaction and Reply Picker */}
          {showPicker && user && (
            <ReactionAndReplyPicker
              onEmojiSelect={handleEmojiSelect}
              onReply={handleReply}
              onClose={() => setShowPicker(false)}
              position={pickerPosition}
              align={isSentByMe ? 'right' : 'left'}
              hasUserReaction={message.reactions?.some(r => r.user_id === user.id)}
              onRemoveReaction={() => {
                const userReaction = message.reactions?.find(r => r.user_id === user.id)
                if (userReaction) {
                  handleToggleFromDisplay(userReaction.emoji)
                }
              }}
              hideReply={hasPaidProduct}
            />
          )}
        </div>
      </div>
    </div>
  )
}
